import { useAction, useCreate, useData, useDelete, useUpdate } from "@inmagik/react-crud"
import { data2formdata, fetchApiJson, normalizeParams } from "@inmagik/react-crud/dist/crud/utils"
import { useMutation } from "@tanstack/react-query"
import { saveAs } from "file-saver"
import { useCallback } from "react"
import { useAuth } from "../auth"
import { API_URL } from "../constants"
import { FileType, Folder, FolderBaseType } from "../types"

export function useFolder(id: string) {
  return useData<Folder>(`${API_URL}/fs/folder/${id}`, { query: {} })
}

export function useDeleteFile() {
  return useDelete(`${API_URL}/fs/file`)
}

export function useUploadFile(id: number) {
  return useAction(`${API_URL}/fs/folder/${id}/upload`, { encode: "formdata" })
}

export function useCreateFolder(id: string) {
  return useCreate<FolderBaseType, { name: string; description: string }>(`${API_URL}/fs/folder/${id}`, {
    encode: "formdata",
  })
}

export function useUpdateFolder() {
  return useUpdate<FolderBaseType, FolderBaseType>(`${API_URL}/fs/folder`, { encode: "formdata" })
}

export function useDeleteFolder() {
  return useDelete(`${API_URL}/fs/folder`)
}

export function useDownloadFile() {
  const { tokens } = useAuth()

  const download = useCallback(
    async (file: FileType, params: Record<string, string | string[] | number[]> = {}) => {
      const qs = normalizeParams(params).toString()
      const res = await fetch(`${API_URL}/fs/file/${file.id}/?${qs}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${tokens?.access}`,
        },
      })
      if (res.ok) {
        const document = await res.blob()
        saveAs(document, `${file.name}${file.ext}`)
      } else {
        console.error(`Errore durante il download del file: ${res.status}`)
      }
    },
    [tokens]
  )

  return download
}

export function useRenameFile() {
  const { tokens } = useAuth()

  return useMutation({
    mutationFn: ({ file, newName, newDescription }: { file: FileType; newName: string; newDescription: string }) => {
      const bodyData = {
        new_name: newName,
        description: newDescription,
      }
      const apiUrl = `${API_URL}/fs/file/${file.id}/rename`
      return fetchApiJson(apiUrl, {
        method: "POST",
        headers: { Authorization: `Bearer ${tokens?.access}` },
        body: data2formdata(bodyData),
      })
    },
  })
}
